import React from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertCircle, Save } from 'lucide-react';
import type { SocialPlatform } from '../icons/SocialIcon';
import { SocialIcon } from '../icons/SocialIcon';

interface SaveDraftInsteadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveDraft: () => void;
  missingPlatforms: SocialPlatform[];
  brandName?: string;
}

export const SaveDraftInsteadModal: React.FC<SaveDraftInsteadModalProps> = ({
  isOpen,
  onClose,
  onSaveDraft,
  missingPlatforms,
  brandName,
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const platformList = missingPlatforms
    .map(p => p.charAt(0).toUpperCase() + p.slice(1))
    .join(', ');

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        <div className="relative bg-white rounded-xl border border-black shadow-2xl max-w-lg w-full p-6 transform transition-all duration-300 ease-in-out">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-yellow-500" />
              <h2 className="text-xl font-semibold text-gray-900">
                {t('modals.saveDraftInstead.title', 'Account Connection Missing')}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {t(
                'modals.saveDraftInstead.message',
                `The selected brand '{{brandName}}' does not have a connected account for the following platform(s): {{platformList}}. You can save this post as a draft and connect the accounts later.`,
                {
                  brandName: brandName || 'Selected Brand',
                  platformList: platformList,
                }
              )}
            </p>
            <div className="flex flex-wrap gap-2 items-center justify-center my-3">
              {missingPlatforms.map(platform => (
                <span key={platform} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                  <SocialIcon platform={platform} size={14} />
                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                  <AlertCircle className="w-3 h-3 text-yellow-600 ml-1" />
                </span>
              ))}
            </div>
            <p className="text-sm text-gray-600">
              {t('modals.saveDraftInstead.question', 'Would you like to save this post as a draft instead?')}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-5 mt-5 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={() => {
                onSaveDraft();
                onClose(); // Close modal after initiating save
              }}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              <Save className="w-4 h-4 mr-2" />
              {t('common.saveDraft', 'Save as Draft')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 